import { makeStyles } from "@fluentui/react-components";
import designTokens from "theme/customTheme";

const contentFadeIn = {
  from: { opacity: 0 },
  to: { opacity: 1 },
};

const contentFadeOut = {
  from: { opacity: 1 },
  to: { opacity: 0 },
};

export const useStyles = makeStyles({
  main: {
    width: designTokens.nav.collapsed,
    minWidth: designTokens.nav.collapsed,
    maxWidth: designTokens.nav.collapsed,
    height: "100vh",
    zIndex: "2",
    display: "flex",
    flexDirection: "column",
    background: `linear-gradient(180deg, ${designTokens.primary[800]} 0%, ${designTokens.primary[900]} 100%)`,
    flexShrink: 0,
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  drawerContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
  navContent: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  navItemsContainer: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    minWidth: 0,
  },
  button: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    height: designTokens.controlHeight.md,
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[2]}`,
    fontSize: designTokens.fontSize.sm,
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    gap: designTokens.spacing[1],
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.neutral[50],
    backgroundColor: "transparent",
    border: "none",
    borderRadius: designTokens.borderRadius.lg,
    transition: `all ${designTokens.transition.base}`,
    cursor: "pointer",
    ":hover": {
      color: designTokens.neutral[50],
      backgroundColor: designTokens.primary[700],
      border: "none",
      cursor: "pointer",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  buttonSelected: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    height: designTokens.controlHeight.md,
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[2]}`,
    fontSize: designTokens.fontSize.sm,
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    gap: designTokens.spacing[1],
    fontWeight: designTokens.fontWeight.normal,
    color: designTokens.neutral[50],
    border: "none",
    borderRadius: designTokens.borderRadius.lg,
    transition: `all ${designTokens.transition.base}`,
    backgroundColor: designTokens.accent[500],
    cursor: "pointer",
    ":hover": {
      color: designTokens.neutral[50],
      backgroundColor: designTokens.accent[500],
      border: "none",
      cursor: "pointer",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  collapsedButton: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    padding: `${designTokens.spacing[2]} 0`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: designTokens.spacing[1],
    borderRadius: "0",
    backgroundColor: "transparent",
    border: "none",
    color: designTokens.neutral[50],
    transition: `background-color ${designTokens.transition.base}, color ${designTokens.transition.base}`,
    cursor: "pointer",
    minHeight: designTokens.controlHeight.md,
    "& .fui-Button__icon": {
      margin: 0,
    },
    ":hover": {
      color: designTokens.neutral[50],
      backgroundColor: designTokens.primary[700],
      border: "none",
      "& svg": {
        color: designTokens.neutral[50],
      },
      "& span": {
        color: designTokens.neutral[50],
      },
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  collapsedButtonIcon: {
    width: designTokens.controlHeight.md,
    minWidth: designTokens.controlHeight.md,
    height: designTokens.controlHeight.md,
    padding: designTokens.spacing[2],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: designTokens.borderRadius.lg,
    backgroundColor: "transparent",
    border: "none",
    color: designTokens.neutral[50],
    cursor: "pointer",
    "& .fui-Button__icon": {
      margin: 0,
    },
    ":hover": {
      color: designTokens.neutral[50],
      backgroundColor: designTokens.primary[700],
      border: "none",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  collapsedNavLabel: {
    fontSize: designTokens.fontSize.xs,
    lineHeight: designTokens.lineHeight.tight,
    color: designTokens.neutral[50],
    textAlign: "center",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%",
    fontWeight: designTokens.fontWeight.normal,
  },
  iconStyle: {
    fontSize: designTokens.iconSize.md,
    color: designTokens.primary[200],
    ":hover": {
      color: designTokens.neutral[50],
    },
  },
  iconStyleSelected: {
    fontSize: designTokens.iconSize.md,
    color: designTokens.neutral[50],
    ":hover": {
      color: designTokens.neutral[50],
    },
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[3]}`,
    transition: `padding ${designTokens.transition.base}`,
    "@media (max-width: 1024px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      paddingLeft: designTokens.spacing[8],
    },
  },
  headerContainerCollapsed: {
    padding: `${designTokens.spacing[4]}`,
    justifyContent: "center",
  },
  logoImage: {
    maxWidth: designTokens.controlHeight.sm,
    height: "auto",
  },
  footerButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[2],
    flexWrap: "wrap",
    paddingBottom: designTokens.spacing[4],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    overflow: "hidden",
    "@media (max-width: 1024px)": {
      flexDirection: "column",
      gap: designTokens.spacing[3],
    },
  },
  divider: {
    color: designTokens.primary[400],
    opacity: 0.25,
    marginBottom: designTokens.spacing[4],
  },
  navItemText: {
    opacity: 1,
    width: "auto",
    overflow: "hidden",
    transition: `opacity ${designTokens.transition.fast}, width ${designTokens.transition.fast}`,
    whiteSpace: "nowrap",
  },
  navItemTextCollapsed: {
    opacity: 0,
    width: "0",
  },
  headerText: {
    opacity: 1,
    width: "auto",
    overflow: "hidden",
    transition: `opacity ${designTokens.transition.fast}, width ${designTokens.transition.fast}`,
    whiteSpace: "nowrap",
    color: designTokens.neutral[50],
    fontSize: designTokens.fontSize.lg,
    fontWeight: designTokens.fontWeight.semibold,
    paddingLeft: designTokens.spacing[2],
  },
  userMenuArea: {
    marginTop: "auto",
    position: "relative",
  },
  desktopUserMenuPopup: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    zIndex: "10",
    marginBottom: designTokens.spacing[1],
  },
  dividerWrapper: {
    height: designTokens.spacing[2],
  },
  dividerWrapperMenuClosed: {
    height: designTokens.spacing[2],
  },
  footerUserInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  footerUserName: {
    color: designTokens.neutral[50],
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chevronIcon: {
    color: designTokens.primary[200],
    flexShrink: 0,
    marginLeft: "auto",
  },
  hamburgerButton: {
    position: "fixed",
    top: designTokens.spacing[2],
    left: designTokens.spacing[4],
    zIndex: "1001",
    minWidth: designTokens.controlHeight.md,
    width: designTokens.controlHeight.md,
    height: designTokens.controlHeight.md,
    backgroundColor: designTokens.primary[800],
    borderRadius: designTokens.borderRadius.lg,
    border: "none",
    color: designTokens.neutral[50],
    boxShadow: designTokens.shadow.lg,
    cursor: "pointer",
    ":hover": {
      backgroundColor: designTokens.primary[700],
      color: designTokens.neutral[50],
      border: "none",
      cursor: "pointer",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  mobileDrawerBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    background: `linear-gradient(180deg, ${designTokens.primary[800]} 0%, ${designTokens.primary[900]} 100%)`,
    padding: 0,
    overflow: "hidden",
    boxSizing: "border-box",
    animationName: contentFadeIn,
    animationDuration: designTokens.transition.base,
    animationTimingFunction: "ease",
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
      animationIterationCount: 1,
    },
  },
  mobileDrawerBodyClosing: {
    animationName: contentFadeOut,
    animationDuration: designTokens.transition.base,
    animationTimingFunction: "ease",
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
      animationIterationCount: 1,
    },
  },
  mobileDrawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[3]} ${designTokens.spacing[4]} ${designTokens.spacing[5]}`,
  },
  mobileDrawerLogoRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  mobileDrawerCloseButton: {
    backgroundColor: "transparent",
    border: "none",
    color: designTokens.neutral[50],
    minWidth: "auto",
    cursor: "pointer",
    borderRadius: designTokens.borderRadius.lg,
    ":hover": {
      backgroundColor: designTokens.primary[700],
      color: designTokens.neutral[50],
      border: "none",
      cursor: "pointer",
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },
  mobileNavContent: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: `0 ${designTokens.spacing[2]}`,
    overflow: "hidden",
  },
  mobileNavItemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[1],
    paddingRight: designTokens.spacing[1],
    overflow: "hidden",
  },
  mobileUserMenuArea: {
    marginTop: "auto",
    position: "relative",
  },
  mobileUserMenuPopup: {
    position: "absolute",
    bottom: "100%",
    left: 0,
    right: 0,
    zIndex: "10",
  },
  mobileDrawer: {
    width: designTokens.drawerWidth.mobile,
    background: "transparent",
    border: "none",
  },
  mobileDrawerBodyInner: {
    background: "transparent",
    padding: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
});
